// ============================================================
// home-search/suggestions.js — Autocompletado y sugerencias de búsqueda
// ============================================================

import { debounce, safeUrl, normalizeText, navigateToBizContext } from './utils.js';
import { isPremium } from './premium-detector.js';
import { openPremiumWindowForBusiness } from './premium-card.js';
import { getApiBaseUrl } from '../api-config.js';

// Estado: últimas sugerencias mostradas para soporte de Enter
let lastSuggestions = { businesses: [], category_suggestions: [], tag_suggestions: [], query: '' };

let _contenedorSugerencias = null;

/**
 * Obtiene (o crea) el contenedor de sugerencias de búsqueda
 */
export function getSuggestionsContainer(inputBusqueda) {
  if (_contenedorSugerencias) return _contenedorSugerencias;

  let container = document.getElementById('search-suggestions');
  if (!container) {
    container = document.createElement('div');
    container.id = 'search-suggestions';
    container.style.cssText = 'position:fixed;background:#fff;border:1px solid #e5e7eb;border-radius:0.75rem;box-shadow:0 16px 36px rgba(0,0,0,0.18);padding:8px;display:none;z-index:999999;max-height:320px;overflow-y:auto;';
    document.body.appendChild(container);

    const place = () => {
      requestAnimationFrame(() => {
        const searchCard = document.querySelector('.search-card');
        const firstField = document.querySelector('.search-card .search-field');
        if (!firstField) return;
        const ffRect = firstField.getBoundingClientRect();
        const isMobile = window.matchMedia && window.matchMedia('(max-width: 640px)').matches;
        const extra = isMobile ? 18 : 8;
        container.style.left = `${Math.round(ffRect.left)}px`;
        container.style.top = `${Math.round(ffRect.bottom + extra)}px`;
        container.style.width = `${Math.round(ffRect.width)}px`;
      });
    };
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
  }

  _contenedorSugerencias = container;
  return container;
}

export function limpiarSugerencias() {
  if (!_contenedorSugerencias) return;
  _contenedorSugerencias.replaceChildren();
  _contenedorSugerencias.style.display = 'none';
  const searchCard = document.querySelector('.search-card');
  if (searchCard) {
    searchCard.style.paddingBottom = '';
    searchCard.style.marginBottom = '';
  }
}

/**
 * Encuentra coincidencia exacta de negocio en las últimas sugerencias
 */
export function findExactBizMatch(q) {
  const nq = normalizeText(q);
  if (!nq) return null;
  const list = (lastSuggestions && Array.isArray(lastSuggestions.businesses)) ? lastSuggestions.businesses : [];
  return list.find(b => normalizeText(b.name || '') === nq) || null;
}

/**
 * Envía evento de clic en tag a analytics sin bloquear la navegación
 */
function trackTagClick(payload) {
  try {
    const url = `${getApiBaseUrl()}/analytics/search/tag-click`;
    const body = JSON.stringify(payload || {});
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(url, blob);
    } else {
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true
      }).catch(() => {});
    }
  } catch (_) { /* noop */ }
}

/**
 * Renderiza las sugerencias (negocios, categorías, palabras)
 */
export function renderSugerencias(data, query) {
  const container = getSuggestionsContainer();
  container.replaceChildren();
  const { businesses = [], category_suggestions = [], tag_suggestions = [] } = data || {};

  if (!businesses.length && !category_suggestions.length && !tag_suggestions.length) {
    limpiarSugerencias();
    return;
  }

  lastSuggestions = {
    businesses: businesses.slice(),
    category_suggestions: category_suggestions.slice(),
    tag_suggestions: tag_suggestions.slice(),
    query
  };
  container.style.display = 'block';

  const searchCard = document.querySelector('.search-card');
  if (searchCard) {
    searchCard.style.paddingBottom = '16px';
    searchCard.style.marginBottom = '8px';
  }
  window.dispatchEvent(new Event('resize'));

  const section = (title) => {
    const h = document.createElement('div');
    h.textContent = title;
    h.style.cssText = 'font-weight:600;font-size:0.85rem;color:#6b7280;padding:6px 8px;';
    return h;
  };

  const svgFor = (kind) => {
    const svgNS = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(svgNS, 'svg');
    svg.setAttribute('width', '16');
    svg.setAttribute('height', '16');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'currentColor');
    svg.style.cssText = 'color:#2563eb;display:inline-block;min-width:16px';

    const path = document.createElementNS(svgNS, 'path');
    switch (kind) {
      case 'biz':
        path.setAttribute('d', 'M10 4h4a2 2 0 012 2v2h3a3 3 0 013 3v7a3 3 0 01-3 3H5a3 3 0 01-3-3v-7a3 3 0 013-3h3V6a2 2 0 012-2zm6 4V6a1 1 0 00-1-1h-4a1 1 0 00-1 1v2h6zm-6 5v2h4v-2h-4z');
        break;
      case 'cat':
        path.setAttribute('d', 'M12 2l9 5-9 5-9-5 9-5zm9 9l-2.74 1.52L12 16l-6.26-3.48L3 11l9 5 9-5zm0 4l-2.74 1.52L12 20l-6.26-3.48L3 15l9 5 9-5z');
        break;
      default:
        path.setAttribute('d', 'M10 2a8 8 0 105.293 14.293l4.707 4.707 1.414-1.414-4.707-4.707A8 8 0 0010 2zm0 2a6 6 0 110 12A6 6 0 0110 4z');
        break;
    }
    svg.appendChild(path);
    return svg;
  };

  const item = (text, onClick, icon = 'search') => {
    const li = document.createElement('div');
    li.style.cssText = 'display:flex;align-items:center;gap:8px;padding:8px;border-radius:8px;cursor:pointer;';
    li.appendChild(svgFor(icon));
    const span = document.createElement('span');
    span.style.cssText = 'font-size:0.95rem;color:#111827';
    span.textContent = text;
    li.appendChild(span);
    li.addEventListener('mouseenter', () => { li.style.background = '#f3f4f6'; });
    li.addEventListener('mouseleave', () => { li.style.background = 'transparent'; });
    li.addEventListener('click', () => { onClick(); limpiarSugerencias(); });
    return li;
  };

  if (businesses.length) {
    container.appendChild(section('Negocios'));
    businesses.slice(0, 5).forEach(b => {
      container.appendChild(item(b.name || '(Sin nombre)', () => {
        try { console.log('👉 [SUG CLICK] negocio:', b?.name, { is_premium: b?.is_premium, isPremium: b?.isPremium, membership: b?.membership, owner_role: b?.owner_role, ownerRole: b?.ownerRole, plan: b?.plan }); } catch(_){}
        if (isPremium(b)) {
          try { console.log('✅ [SUG CLICK] premium detectado, abriendo ventana'); } catch(_){}
          openPremiumWindowForBusiness(b);
          return;
        }
        if (!navigateToBizContext(b)) {
          const inputBusqueda = document.querySelector('.search-card input[type="text"]');
          if (inputBusqueda) inputBusqueda.value = b.name || query;
          // Disparar hacerBusqueda — se importa dinámicamente para evitar circular
          import('./search-flow.js').then(m => m.hacerBusqueda()).catch(() => {});
        }
      }, 'biz'));
    });
  }

  if (category_suggestions.length) {
    container.appendChild(section('Categorías'));
    category_suggestions.slice(0, 5).forEach(c => {
      container.appendChild(item(c.name || c.slug, () => {
        const qn = (query || '').toLowerCase();
        const nameMatch = (c.name || '').toLowerCase() === qn;
        const isLikelySub = /s$/.test(c.slug) || /-/.test(c.slug);
        if (nameMatch || isLikelySub) {
          window.location.href = `/categories.html?subcategory=${encodeURIComponent(c.slug)}`;
        } else {
          window.location.href = `/categories.html?category=${encodeURIComponent(c.slug)}`;
        }
      }, 'cat'));
    });
  }

  if (tag_suggestions.length) {
    container.appendChild(section('Palabras'));
    tag_suggestions.slice(0, 6).forEach(t => {
      container.appendChild(item(t.etiqueta, () => {
        try {
          const inputBusqueda = document.querySelector('.search-card input[type="text"]');
          const inputCiudad = document.querySelectorAll('.search-card input[type="text"]')[1];
          const qNow = (inputBusqueda && inputBusqueda.value || '').trim();
          const cityRaw = (typeof inputCiudad !== 'undefined' && inputCiudad && inputCiudad.value) ? inputCiudad.value.trim() : '';
          let ciudad = null, pais = null;
          if (cityRaw) {
            if (cityRaw.includes(',')) {
              const parts = cityRaw.split(',');
              ciudad = (parts[0] || '').trim() || null;
              pais = (parts[1] || '').trim() || null;
            } else {
              ciudad = cityRaw;
            }
          }
          const payload = {
            etiqueta: t.etiqueta,
            category_slug: t.category_slug || t.categorySlug || null,
            categoria_id: t.categoria_id || null,
            q: qNow || null,
            ciudad,
            pais,
            ts: Date.now()
          };
          trackTagClick(payload);
        } catch (_) { /* noop */ }

        const catSlug = t.category_slug || t.categorySlug || null;
        if (catSlug) {
          window.location.href = `/categories.html?category=${encodeURIComponent(catSlug)}`;
          return;
        }

        const etiqueta = (t.etiqueta || '').toLowerCase();
        const subcatMap = {
          'arriendo': 'arriendos', 'arriendos': 'arriendos', 'alquiler': 'arriendos',
          'renta': 'arriendos', 'rent a car': 'alquiler-vehiculos',
          'hotel': 'hoteles', 'hostal': 'hostales', 'glamping': 'glamping'
        };
        const subSlug = subcatMap[etiqueta];
        if (subSlug) {
          window.location.href = `/categories.html?subcategory=${encodeURIComponent(subSlug)}`;
          return;
        }

        const inputBusqueda = document.querySelector('.search-card input[type="text"]');
        if (inputBusqueda) {
          inputBusqueda.value = t.etiqueta;
          import('./search-flow.js').then(m => m.hacerBusqueda()).catch(() => {});
        }
      }, 'fa-hashtag'));
    });
  }
}

/**
 * Busca sugerencias desde el backend (debounced)
 */
export function buscarSugerencias(inputBusqueda, inputCiudad) {
  const debouncedFetch = debounce(async () => {
    let q = inputBusqueda.value.trim();
    q = typeof window.Sanitizer !== 'undefined' ? window.Sanitizer.sanitizeSQL(q) : q;
    if (q.length < 2) {
      limpiarSugerencias();
      return;
    }
    try {
      const categoria = (() => {
        const el = document.querySelector('.search-card select');
        if (!el) return '';
        const v = (el.value || '').trim().toLowerCase();
        return (v === 'seleccionar categoria' || v === 'seleccionar categoría') ? '' : v;
      })();
      const categoriaSel = categoria ? `&categoria=${encodeURIComponent(categoria)}` : '';
      const cityRaw = (inputCiudad && inputCiudad.value.trim()) || '';
      let paramsCiudad = '';
      if (cityRaw) {
        if (cityRaw.includes(',')) {
          const [city, country] = cityRaw.split(',');
          paramsCiudad = `&ciudad=${encodeURIComponent(city.trim())}&pais=${encodeURIComponent(country.trim())}`;
        } else {
          paramsCiudad = `&ciudad=${encodeURIComponent(cityRaw)}`;
        }
      }
      const url = `${window.apiConfig?.baseURL || '/api/v1'}/search?q=${encodeURIComponent(q)}&limit=8${categoriaSel}${paramsCiudad}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error('Error buscando sugerencias');
      const data = await res.json();
      renderSugerencias(data, q);
    } catch (e) {
      limpiarSugerencias();
      // Mostrar banner si el backend está caído
      import('./backend-banner.js').then(m => m.testBackend()).catch(() => {});
    }
  }, 250);

  return debouncedFetch;
}
